// Тонкий API-слой админки Web App (Этап 1.7).
// Все вызовы идут на /api/mgmt/* (nginx 1.6 → aiohttp subapp /mgmt).
// Модель проекта: initData кладём в ТЕЛО POST (как остальные /api/-вызовы),
// бэкенд-гард достаёт его оттуда. Токен сессии живёт ТОЛЬКО в памяти модуля.
import { getWebApp } from './telegram'

// Токен IN-MEMORY: by design. Перезагрузка вебки = переменная сброшена =
// повторный ввод PIN. НЕ localStorage/sessionStorage (XSS/persist-риск).
// В консоль/лог токен НЕ пишем НИГДЕ.
let _token = null

const BASE = '/api/mgmt'
const SESSION_HEADER = 'X-Admin-Session'

// initData всегда свежий из Telegram WebApp (его подписал Telegram).
function initData() {
  return getWebApp().initData || ''
}

// Единая POST-обёртка. withToken=true добавляет X-Admin-Session (для роутов
// под полным гардом: refresh/logout). Тело — JSON, ошибки сети не валят UI.
async function post(path, body, withToken = false) {
  const headers = { 'Content-Type': 'application/json' }
  if (withToken && _token) headers[SESSION_HEADER] = _token
  return fetch(`${BASE}${path}`, {
    method: 'POST',
    headers,
    body: JSON.stringify({ initData: initData(), ...body }),
  })
}

// Безопасный разбор JSON: пустое/битое тело → {} (статус важнее тела).
async function readJson(response) {
  return response.json().catch(() => ({}))
}

// whoami — «я в белом списке админов?». Гард no_session (0-1-2): 200 = да.
// Сессия НЕ нужна. Любая сетевая ошибка / не-200 → false (шестерёнку прячем).
export async function whoami() {
  try {
    const response = await post('/whoami', {})
    return response.status === 200
  } catch {
    return false
  }
}

// login — вход по PIN. 200 → прячем token В ПАМЯТЬ, отдаём {ok:true}.
// Иначе {ok:false, reason, status} — UI мапит 401/423/409/404/403 на экраны.
export async function login(pin) {
  try {
    const response = await post('/pin/login', { pin })
    const data = await readJson(response)
    if (response.status === 200 && data.token) {
      _token = data.token // в консоль/лог НЕ пишем
      return { ok: true }
    }
    return { ok: false, reason: data.reason || null, status: response.status }
  } catch {
    return { ok: false, reason: 'network', status: 0 }
  }
}

// enroll — установка первого PIN (пустой слот). 200 → ok; 409 = уже задан.
export async function enroll(pin) {
  try {
    const response = await post('/pin/enroll', { pin })
    const data = await readJson(response)
    return {
      ok: response.status === 200,
      reason: data.reason || null,
      status: response.status,
    }
  } catch {
    return { ok: false, reason: 'network', status: 0 }
  }
}

// biometricLogin — вход по сохранённому biometric credential. 200 → token в память,
// формат успеха такой же, как у PIN-login. Secret в лог/консоль НЕ пишем.
export async function biometricLogin(credentialId, secret) {
  try {
    const response = await post('/biometric/login', { credential_id: credentialId, secret })
    const data = await readJson(response)
    if (response.status === 200 && data.token) {
      _token = data.token
      return { ok: true }
    }
    if (response.status === 401) {
      return { ok: false, error: 'biometric_failed', status: response.status }
    }
    if (response.status === 503) {
      return { ok: false, error: 'biometric_unavailable', status: response.status }
    }
    return { ok: false, error: data.error || null, status: response.status }
  } catch {
    return { ok: false, error: 'network', status: 0 }
  }
}

// biometricEnroll — создать credential после живой admin-сессии. Raw secret
// возвращаем только вызывающему UI для записи в BiometricManager, не логируем.
export async function biometricEnroll(deviceLabel) {
  try {
    const response = await post('/biometric/enroll', { device_label: deviceLabel }, true)
    const data = await readJson(response)
    if (response.status === 200 && data.credential_id && data.secret) {
      return { ok: true, credentialId: data.credential_id, secret: data.secret }
    }
    return { ok: false, status: response.status, error: data.error || null }
  } catch {
    return { ok: false, status: 0, error: 'network' }
  }
}

// biometricRevoke — отозвать credentials на бэке. Под полным гардом, нужен token.
export async function biometricRevoke() {
  try {
    const response = await post('/biometric/revoke', {}, true)
    const data = await readJson(response)
    if (response.status === 200) {
      return { ok: !!data.ok, revoked: data.revoked }
    }
    return { ok: false, status: response.status, error: data.error || null }
  } catch {
    return { ok: false, status: 0, error: 'network' }
  }
}

// refresh — heartbeat idle-сессии. Без токена бессмысленно → false сразу.
export async function refresh() {
  if (!_token) return false
  try {
    const response = await post('/session/refresh', {}, true)
    return response.status === 200
  } catch {
    return false
  }
}

// logout — выход. Токен чистим ВСЕГДА (даже если запрос упал — локально вышли).
export async function logout() {
  if (!_token) return
  try {
    await post('/session/logout', {}, true)
  } catch {
    // сеть упала — всё равно выходим локально
  } finally {
    _token = null
  }
}

// statsOverview — дашборд за период (Этап 2). Под ПОЛНЫМ гардом → withToken=true
// (X-Admin-Session обязателен). 200 → распарсенный JSON {ok, period, stats};
// иначе {error:true, status} — UI мапит: 401 = сессия протухла (→ назад на PIN),
// прочее = общая ошибка загрузки. Сетевой сбой → status:0.
export async function statsOverview(period) {
  try {
    const response = await post('/stats/overview', { period }, true)
    if (response.status === 200) {
      return await readJson(response)
    }
    return { error: true, status: response.status }
  } catch {
    return { error: true, status: 0 }
  }
}

// statsFinance — финансовая сводка по пополнениям за период (Этап 2). Под ПОЛНЫМ
// гардом → withToken=true (X-Admin-Session обязателен), как statsOverview. 200 →
// распарсенный JSON {ok, period, deposits_total, deposits_promo, deposits_real,
// by_gateway}; иначе {error:true, status} — UI мапит: 401 = сессия протухла
// (→ назад на PIN), прочее = общая ошибка загрузки. Сетевой сбой → status:0.
export async function statsFinance(period) {
  try {
    const response = await post('/stats/finance', { period }, true)
    if (response.status === 200) {
      return await readJson(response)
    }
    return { error: true, status: response.status }
  } catch {
    return { error: true, status: 0 }
  }
}

// statsBalances — два безопасных баланса (D users / C bot stars). Под ПОЛНЫМ
// гардом → withToken=true (X-Admin-Session обязателен), как statsFinance. 200 →
// распарсенный JSON {ok, total_users_balance_rub, bot_stars_balance,
// bot_stars_balance_available, bot_stars_balance_error?}; иначе {error:true,
// status} — UI мапит: 401 = сессия протухла, прочее = общая ошибка загрузки.
// Сетевой сбой → status:0.
export async function statsBalances() {
  try {
    const response = await post('/stats/balances', {}, true)
    if (response.status === 200) {
      return await readJson(response)
    }
    return { error: true, status: response.status }
  } catch {
    return { error: true, status: 0 }
  }
}

// statsSales — продажи по категориям за период (Этап 2). Под ПОЛНЫМ гардом →
// withToken=true (X-Admin-Session обязателен), как statsFinance. 200 →
// распарсенный JSON {ok, period, categories[], revenue_total, profit_total};
// иначе {error:true, status} — UI мапит: 401 = сессия протухла (→ назад на PIN),
// прочее = общая ошибка загрузки. Сетевой сбой → status:0.
export async function statsSales(period) {
  try {
    const response = await post('/stats/sales', { period }, true)
    if (response.status === 200) {
      return await readJson(response)
    }
    return { error: true, status: response.status }
  } catch {
    return { error: true, status: 0 }
  }
}

// statsPeople — раздел «Люди»: счётчики юзеров + рейтинги (Этап 2). Под ПОЛНЫМ
// гардом → withToken=true (X-Admin-Session обязателен), как statsSales. 200 →
// распарсенный JSON {ok, period, counters, scope, top_buyers_period, tops};
// иначе {error:true, status} — UI мапит: 401 = сессия протухла (→ назад на PIN),
// прочее = общая ошибка загрузки. Сетевой сбой → status:0.
export async function statsPeople(period) {
  try {
    const response = await post('/stats/people', { period }, true)
    if (response.status === 200) {
      return await readJson(response)
    }
    return { error: true, status: response.status }
  } catch {
    return { error: true, status: 0 }
  }
}

// statsJournal — раздел «Журнал»: лента последних событий (Этап 2). Под ПОЛНЫМ
// гардом → withToken=true (X-Admin-Session обязателен), как statsPeople. ЛЕНТА,
// не период: тело {limit, offset, event_type, filter_user_id} (пагинация + фильтры),
// бэк клампит limit 1..100. 200 → {ok, events[], limit, offset, filter, has_more};
// иначе {error:true, status} — UI мапит: 401 = сессия протухла (→ назад на PIN),
// прочее = общая ошибка загрузки. Сетевой сбой → status:0.
export async function statsJournal({ limit, offset, event_type, filter_user_id } = {}) {
  try {
    const response = await post(
      '/stats/journal',
      { limit, offset, event_type, filter_user_id },
      true,
    )
    if (response.status === 200) {
      return await readJson(response)
    }
    return { error: true, status: response.status }
  } catch {
    return { error: true, status: 0 }
  }
}

// statsUtm — раздел «Источники (UTM)» (Этап 3). Под ПОЛНЫМ гардом →
// withToken=true (X-Admin-Session обязателен), как statsJournal. Теперь умеет
// period: today/week/month/all. 200 → распарсенный JSON
// {ok, generated_for, period, totals, sources[], deeplinks[]}; drill-down
// делает КЛИЕНТ из этого же объекта (второго запроса нет). Иначе {error:true,
// status} — UI мапит: 401 = сессия протухла (→ назад на PIN), прочее = общая
// ошибка загрузки. Сетевой сбой → status:0.
export async function statsUtm(period) {
  try {
    const response = await post('/stats/utm', { period }, true)
    if (response.status === 200) {
      return await readJson(response)
    }
    return { error: true, status: response.status }
  } catch {
    return { error: true, status: 0 }
  }
}

// statsLoyalty — раздел «Лояльность»: read-аналитика кэшбека по тирам (Этап 3,
// доперенос из бота loyalty_menu в Аналитику). Под ПОЛНЫМ гардом → withToken=true,
// как statsUtm/statsFinance. Период передаётся (по выбранному period). Конфиг
// (пороги/проценты) приходит в settings ТОЛЬКО для показа — НЕ редактируем.
// 200 → {ok, period, levels[4], repeat, cashback:{total,last_7d,last_30d,period,
// by_tier[4]}, settings:{enabled,thresholds,percents,tier_names}}. Деньги СТРОКОЙ
// (_money_str), UI Number(). Иначе {error:true, status} — UI мапит: 401 = протух
// (→ PIN), прочее = ошибка загрузки. Сетевой сбой → status:0.
export async function statsLoyalty(period) {
  try {
    const response = await post('/stats/loyalty', { period }, true)
    if (response.status === 200) {
      return await readJson(response)
    }
    return { error: true, status: response.status }
  } catch {
    return { error: true, status: 0 }
  }
}

// promoList — раздел «Промокоды» (Этап 3, ПЕРВЫЙ write-раздел; пока только READ-
// список). Под ПОЛНЫМ гардом → withToken=true (X-Admin-Session обязателен), как
// statsUtm. ALL-TIME: периода НЕТ (тело пустое). 200 → распарсенный JSON
// {ok, promos:[{id, code, reward(строка), max_uses, used, remaining, is_active}]}.
// Иначе {error:true, status} — UI мапит: 401 = сессия протухла (→ назад на PIN),
// прочее = общая ошибка загрузки. Сетевой сбой → status:0.
export async function promoList() {
  try {
    const response = await post('/promo/list', {}, true)
    if (response.status === 200) {
      return await readJson(response)
    }
    return { error: true, status: response.status }
  } catch {
    return { error: true, status: 0 }
  }
}

// promoCreate — создать промокод (Этап 3 write). withToken. Бэк сам нормализует
// код в UPPER и валидирует — клиент НЕ авторитет. 200 → {ok:true, promo}. 400
// (валидация) / 409 (дубль) → {ok:false, status, message} — message из бэкового
// {ok:false, error:"..."}, форма покажет его юзеру (что не так / занят). 401 →
// {ok:false, status:401} (сессия протухла → PIN). Сеть → status:0.
export async function promoCreate({ code, reward, max_uses }) {
  try {
    const response = await post('/promo/create', { code, reward, max_uses }, true)
    const data = await readJson(response)
    if (response.status === 200 && data.ok) {
      return { ok: true, promo: data.promo }
    }
    return { ok: false, status: response.status, message: data.error || null }
  } catch {
    return { ok: false, status: 0, message: null }
  }
}

// promoDeactivate — is_active=0 (обратимо, без подтверждения — контент). 200 →
// {ok:true}. Иначе {ok:false, status, message} (404 нет кода / 401 протух / др.).
export async function promoDeactivate(code) {
  try {
    const response = await post('/promo/deactivate', { code }, true)
    const data = await readJson(response)
    if (response.status === 200 && data.ok) {
      return { ok: true }
    }
    return { ok: false, status: response.status, message: data.error || null }
  } catch {
    return { ok: false, status: 0, message: null }
  }
}

// promoReactivate — is_active=1. Зеркало deactivate.
export async function promoReactivate(code) {
  try {
    const response = await post('/promo/reactivate', { code }, true)
    const data = await readJson(response)
    if (response.status === 200 && data.ok) {
      return { ok: true }
    }
    return { ok: false, status: response.status, message: data.error || null }
  } catch {
    return { ok: false, status: 0, message: null }
  }
}

// userCard — карточка пользователя (Этап 3, раздел Управление → Пользователи).
// СЛОЙ 1: поиск + read-карточка (без действий). Под ПОЛНЫМ гардом → withToken=true,
// как promoList. Тело {query} — username или ID (бэк сам решает по isdigit).
// 200 → {ok:true, user, purchases, deposits} (деньги СТРОКАМИ, UI парсит Number()).
// 404 → {ok:false, status:404, message} — юзер не найден (не ошибка загрузки, своя
// ветка в UI: показать «не найден», поле оставить). 401 → {ok:false, status:401}
// (сессия протухла → PIN). Прочее/сеть → {ok:false, status, message:null}.
export async function userCard(query) {
  try {
    const response = await post('/user/card', { query }, true)
    const data = await readJson(response)
    if (response.status === 200 && data.ok) {
      return { ok: true, user: data.user, purchases: data.purchases, deposits: data.deposits }
    }
    return { ok: false, status: response.status, message: data.error || null }
  } catch {
    return { ok: false, status: 0, message: null }
  }
}

// userUsernameRefresh — освежить @username из Telegram API (Слой 2). withToken.
// 200 {ok:true, username} — обновлено (username может быть null). 200 {ok:false,
// error} — юзер недоступен/не писал боту (НЕ паника, мягкое сообщение). 400 —
// невалидный user_id. 401 → протух. message — текст бэка для показа.
export async function userUsernameRefresh(user_id) {
  try {
    const response = await post('/user/username/refresh', { user_id }, true)
    const data = await readJson(response)
    if (response.status === 200 && data.ok) {
      return { ok: true, username: data.username }
    }
    return { ok: false, status: response.status, message: data.error || null }
  } catch {
    return { ok: false, status: 0, message: null }
  }
}

// userBan — забанить (Слой 2, деструктивное → подтверждение НА ФРОНТЕ). withToken.
// comment опционален. 200 {ok:true, is_banned:true, ban_comment}. 400 невалид /
// 401 протух → {ok:false, status, message}.
export async function userBan(user_id, comment) {
  try {
    const response = await post('/user/ban', { user_id, comment }, true)
    const data = await readJson(response)
    if (response.status === 200 && data.ok) {
      return { ok: true, is_banned: data.is_banned, ban_comment: data.ban_comment }
    }
    return { ok: false, status: response.status, message: data.error || null }
  } catch {
    return { ok: false, status: 0, message: null }
  }
}

// userUnban — снять бан (Слой 2, разблокирующее → БЕЗ подтверждения). withToken.
// 200 {ok:true, is_banned:false}. 400 / 401 → {ok:false, status, message}.
export async function userUnban(user_id) {
  try {
    const response = await post('/user/unban', { user_id }, true)
    const data = await readJson(response)
    if (response.status === 200 && data.ok) {
      return { ok: true, is_banned: data.is_banned }
    }
    return { ok: false, status: response.status, message: data.error || null }
  } catch {
    return { ok: false, status: 0, message: null }
  }
}

export async function userReferralBlock(user_id, comment) {
  try {
    const response = await post('/user/referral/block', { user_id, comment }, true)
    const data = await readJson(response)
    if (response.status === 200 && data.ok) return { ok: true, is_referral_blocked: data.is_referral_blocked }
    return { ok: false, status: response.status, message: data.error || null }
  } catch { return { ok: false, status: 0, message: null } }
}

export async function userReferralUnblock(user_id) {
  try {
    const response = await post('/user/referral/unblock', { user_id }, true)
    const data = await readJson(response)
    if (response.status === 200 && data.ok) return { ok: true, is_referral_blocked: data.is_referral_blocked }
    return { ok: false, status: response.status, message: data.error || null }
  } catch { return { ok: false, status: 0, message: null } }
}

// userBalanceAdd — выдать на основной баланс (Слой 3 ДЕНЬГИ, подтв+красная НА
// ФРОНТЕ). withToken. Бэк валидирует сумму (>0, ≤20000) и пишет log_deposit
// ('manual_admin' → видно в статистике). 200 {ok:true, balance}. 400 (невалид
// сумма) / 404 (юзер) / 401 (протух) → {ok:false, status, message}.
export async function userBalanceAdd(user_id, amount) {
  try {
    const response = await post('/user/balance/add', { user_id, amount }, true)
    const data = await readJson(response)
    if (response.status === 200 && data.ok) {
      return { ok: true, balance: data.balance }
    }
    return { ok: false, status: response.status, message: data.error || null }
  } catch {
    return { ok: false, status: 0, message: null }
  }
}

// userBalanceRemove — снять с ОСНОВНОГО баланса (Слой 3, подтв+красная). withToken.
// Бэк: try_decrement_main_balance (атомарно, ТОЛЬКО основной balance, не в минус).
// 200 {ok:true, balance}. 409 — недостаточно средств на основном (message +
// текущий balance). 400/404/401 → {ok:false, status, message}.
export async function userBalanceRemove(user_id, amount) {
  try {
    const response = await post('/user/balance/remove', { user_id, amount }, true)
    const data = await readJson(response)
    if (response.status === 200 && data.ok) {
      return { ok: true, balance: data.balance }
    }
    return { ok: false, status: response.status, message: data.error || null }
  } catch {
    return { ok: false, status: 0, message: null }
  }
}

// userBalanceSet — задать точное значение основного баланса (Слой 3, подтв+красная).
// withToken. amount ≥ 0. Без увед юзеру. 200 {ok:true, balance}. 400/404/401 →
// {ok:false, status, message}.
export async function userBalanceSet(user_id, amount) {
  try {
    const response = await post('/user/balance/set', { user_id, amount }, true)
    const data = await readJson(response)
    if (response.status === 200 && data.ok) {
      return { ok: true, balance: data.balance }
    }
    return { ok: false, status: response.status, message: data.error || null }
  } catch {
    return { ok: false, status: 0, message: null }
  }
}

// recipientList — заблокированные получатели (Этап 3, раздел Управление →
// Блокировки получателей). По НИКУ (recipient_blocks), отдельно от бана.
// Под ПОЛНЫМ гардом → withToken=true, как promoList. ALL-TIME: тело пустое.
// 200 → {ok:true, blocks:[{username, comment, is_active, created_at(ISO|null)}]}.
// Иначе {error:true, status} — UI мапит: 401 = протух (→ PIN), прочее = ошибка.
export async function recipientList() {
  try {
    const response = await post('/recipient/list', {}, true)
    if (response.status === 200) {
      return await readJson(response)
    }
    return { error: true, status: response.status }
  } catch {
    return { error: true, status: 0 }
  }
}

// recipientBlock — заблокировать получателя по нику (Этап 3 write, деструктивное
// → подтверждение НА ФРОНТЕ). withToken. Бэк нормализует ник. comment опционален.
// 200 {ok:true, username}. 400 (пустой/не записан) / 401 протух → {ok:false,
// status, message} (message из бэкового error). Сеть → status:0.
export async function recipientBlock(username, comment) {
  try {
    const response = await post('/recipient/block', { username, comment }, true)
    const data = await readJson(response)
    if (response.status === 200 && data.ok) {
      return { ok: true, username: data.username }
    }
    return { ok: false, status: response.status, message: data.error || null }
  } catch {
    return { ok: false, status: 0, message: null }
  }
}

// recipientUnblock — разблокировать получателя (is_active=0, разблокирующее →
// БЕЗ подтверждения). withToken. Идемпотентно. 200 {ok:true, username}.
// 400 / 401 → {ok:false, status, message}.
export async function recipientUnblock(username) {
  try {
    const response = await post('/recipient/unblock', { username }, true)
    const data = await readJson(response)
    if (response.status === 200 && data.ok) {
      return { ok: true, username: data.username }
    }
    return { ok: false, status: response.status, message: data.error || null }
  } catch {
    return { ok: false, status: 0, message: null }
  }
}

// utmGenerate — построить startapp-ссылку в витрину (Этап 3, Управление →
// UTM-ссылки). ЧИСТЫЙ ГЕНЕРАТОР: в БД ничего не пишет, бэк строит+валидирует
// (клиент не авторитет). withToken. target ∈ home/stars/premium/gifts. 200
// {ok:true, link, startapp}. 400 (невалид label/target) / 401 (протух) →
// {ok:false, status, message} — message из бэкового error (показать в форме).
export async function utmGenerate({ label, target }) {
  try {
    const response = await post('/utm/generate', { label, target }, true)
    const data = await readJson(response)
    if (response.status === 200 && data.ok) {
      return { ok: true, link: data.link, startapp: data.startapp }
    }
    return { ok: false, status: response.status, message: data.error || null }
  } catch {
    return { ok: false, status: 0, message: null }
  }
}

// channelList — обязательные каналы (Этап 3, Управление → Каналы). ВСЕ каналы
// (вкл. деактивированные), полные поля. Под ПОЛНЫМ гардом → withToken=true, как
// recipientList. ALL-TIME: тело пустое. 200 → {ok:true, channels:[{channel_id,
// channel_url, channel_name, is_active, added_time(epoch|null)}]}. Иначе
// {error:true, status} — UI мапит: 401 = протух (→ PIN), прочее = ошибка.
export async function channelList() {
  try {
    const response = await post('/channel/list', {}, true)
    if (response.status === 200) {
      return await readJson(response)
    }
    return { error: true, status: response.status }
  } catch {
    return { error: true, status: 0 }
  }
}

// channelAdd — добавить обязательный канал (Этап 3 write). БЕЗ подтверждения.
// withToken. Бэк валидирует формат + bot-валидацию (get_chat/бот-админ) — клиент
// НЕ авторитет. 200 {ok:true, channel}. 400 (невалид формат) / 409 (дубль) /
// 200 {ok:false} (канал недоступен/бот не админ) → {ok:false, status, message}.
// 401 → протух. message — текст бэка для показа в форме.
export async function channelAdd({ channel_id, channel_url, channel_name }) {
  try {
    const response = await post('/channel/add',
      { channel_id, channel_url, channel_name }, true)
    const data = await readJson(response)
    if (response.status === 200 && data.ok) {
      return { ok: true, channel: data.channel }
    }
    return { ok: false, status: response.status, message: data.error || null }
  } catch {
    return { ok: false, status: 0, message: null }
  }
}

// channelDeactivate — is_active=0 (обратимо, выключающее → подтверждение НА
// ФРОНТЕ + красная). withToken. 200 {ok:true}. 400/404/401 → {ok:false,...}.
export async function channelDeactivate(channel_id) {
  try {
    const response = await post('/channel/deactivate', { channel_id }, true)
    const data = await readJson(response)
    if (response.status === 200 && data.ok) {
      return { ok: true }
    }
    return { ok: false, status: response.status, message: data.error || null }
  } catch {
    return { ok: false, status: 0, message: null }
  }
}

// channelReactivate — is_active=1. Зеркало deactivate, БЕЗ подтверждения.
export async function channelReactivate(channel_id) {
  try {
    const response = await post('/channel/reactivate', { channel_id }, true)
    const data = await readJson(response)
    if (response.status === 200 && data.ok) {
      return { ok: true }
    }
    return { ok: false, status: response.status, message: data.error || null }
  } catch {
    return { ok: false, status: 0, message: null }
  }
}

// reviewList — раздел «Отзывы» (Этап 3, Управление 6-й подраздел). Список
// ОПУБЛИКОВАННЫХ отзывов из НАШЕЙ БД (админ видит, что удаляет). Под ПОЛНЫМ
// гардом → withToken=true, как channelList. ALL-TIME: тело пустое. 200 →
// {ok:true, reviews:[{review_number, text, username, user_id, rating_category,
// product_name, created_at(epoch)}]}. Иначе {error:true, status} — UI мапит:
// 401 = протух (→ PIN), прочее = ошибка загрузки. Сетевой сбой → status:0.
export async function reviewList() {
  try {
    const response = await post('/review/list', {}, true)
    if (response.status === 200) {
      return await readJson(response)
    }
    return { error: true, status: response.status }
  } catch {
    return { error: true, status: 0 }
  }
}

// reviewDelete — удалить отзыв с публичного сайта (Этап 3 write, НЕОБРАТИМО →
// подтверждение + красная НА ФРОНТЕ). withToken. Бэк делает DELETE на внешний
// API (токен в header, наружу не светит) + помечает локально deleted. 200
// {ok:true, review_number}. 400 (невалид номер) / 404 (нет на сайте) / 502
// (внешний сбой/внешняя 401) / 200-ok:false (сеть) → {ok:false, status, message}.
// 401 = протухшая СЕССИЯ (→ PIN); внешний 401 бэк маскирует в 502 — НЕ путаем.
export async function reviewDelete(review_number) {
  try {
    const response = await post('/review/delete', { review_number }, true)
    const data = await readJson(response)
    if (response.status === 200 && data.ok) {
      return { ok: true, review_number: data.review_number }
    }
    return { ok: false, status: response.status, message: data.error || null }
  } catch {
    return { ok: false, status: 0, message: null }
  }
}

// settingsCashbackGet — текущий конфиг кэшбека (Этап 3, таб «Настройки» →
// Кэшбек). Под ПОЛНЫМ гардом → withToken=true, как reviewList. Тело пустое.
// 200 → {ok:true, enabled(bool), t1,t2,t3(пороги ₽, числа), p0,p1,p2,p3(%, числа)}.
// Это КОНФИГ, не деньги-баланс → числа, не строки. Иначе {error:true, status} —
// UI мапит: 401 = протух (→ PIN), прочее = ошибка загрузки. Сеть → status:0.
export async function settingsCashbackGet() {
  try {
    const response = await post('/settings/cashback/get', {}, true)
    if (response.status === 200) {
      return await readJson(response)
    }
    return { error: true, status: response.status }
  } catch {
    return { error: true, status: 0 }
  }
}

// settingsCashbackSet — сохранить конфиг кэшбека (Этап 3 write). withToken.
// Бэк — авторитет валидации: пороги 0≤t1<t2<t3 строго, проценты 0..100; клиент
// лишь подсказывает. Выключение кэшбека (enabled on→off) на ФРОНТЕ требует
// подтверждения + красная (выключающее). 200 {ok:true, ...конфиг}. 400
// (валидация) → {ok:false, status, message} (message из бэкового error, форма
// покажет). 401 → протух (→ PIN). Сеть → status:0.
export async function settingsCashbackSet({ enabled, t1, t2, t3, p0, p1, p2, p3 }) {
  try {
    const response = await post('/settings/cashback/set',
      { enabled, t1, t2, t3, p0, p1, p2, p3 }, true)
    const data = await readJson(response)
    if (response.status === 200 && data.ok) {
      return { ok: true, settings: data }
    }
    return { ok: false, status: response.status, message: data.error || null }
  } catch {
    return { ok: false, status: 0, message: null }
  }
}

// settingsReferralTextGet — текущий текст реф-приглашения (Этап 3, Настройки →
// Реф-текст). Под ПОЛНЫМ гардом → withToken=true, как settingsCashbackGet. Тело
// пустое. 200 → {ok:true, text, max_len}. Иначе {error:true, status} — UI мапит:
// 401 = протух (→ PIN), прочее = ошибка загрузки. Сеть → status:0.
export async function settingsReferralTextGet() {
  try {
    const response = await post('/settings/referral-text/get', {}, true)
    if (response.status === 200) {
      return await readJson(response)
    }
    return { error: true, status: response.status }
  } catch {
    return { error: true, status: 0 }
  }
}

// settingsReferralTextSet — сохранить текст реф-приглашения (Этап 3 write).
// withToken. Бэк — авторитет валидации (зеркало бота: не пусто, ≤200, без < >);
// клиент лишь хинт. БЕЗ подтверждения (текст, обратимо). 200 {ok:true, text,
// max_len}. 400 (валидация) → {ok:false, status, message} (message из бэкового
// error). 401 → протух (→ PIN). Сеть → status:0.
export async function settingsReferralTextSet(text) {
  try {
    const response = await post('/settings/referral-text/set', { text }, true)
    const data = await readJson(response)
    if (response.status === 200 && data.ok) {
      return { ok: true, text: data.text, max_len: data.max_len }
    }
    return { ok: false, status: response.status, message: data.error || null }
  } catch {
    return { ok: false, status: 0, message: null }
  }
}

// settingsWelcomeBonusGet — текущий welcome-конфиг (Этап 3, Настройки →
// Welcome-бонус). Полный гард → withToken. Пустое тело. 200 → {ok:true,
// enabled, min_amount, percent, duration_minutes, duration_max}. Иначе
// {error:true, status}. UI: 401 = протух (→ PIN), прочее = ошибка загрузки.
export async function settingsWelcomeBonusGet() {
  try {
    const response = await post('/settings/welcome-bonus/get', {}, true)
    if (response.status === 200) {
      return await readJson(response)
    }
    return { error: true, status: response.status }
  } catch {
    return { error: true, status: 0 }
  }
}

// settingsWelcomeBonusSet — групповой set 4 ключей. Бэк — авторитет валидации
// (min_amount≥0, percent 0..100, duration 1..10080), сериализация чисел как бот.
// Подтверждение/красная — ТОЛЬКО при выключении toggle (off), на UI. Тут —
// чистый сетевой слой. 200 {ok:true, ...значения после set}. 400 → {ok:false,
// status:400, message}. 401 → протух (→ PIN). Сеть → status:0.
export async function settingsWelcomeBonusSet(payload) {
  try {
    const response = await post('/settings/welcome-bonus/set', payload, true)
    const data = await readJson(response)
    if (response.status === 200 && data.ok) {
      return {
        ok: true,
        enabled: !!data.enabled,
        min_amount: Number(data.min_amount),
        percent: Number(data.percent),
        duration_minutes: Number(data.duration_minutes),
        duration_max: Number(data.duration_max),
      }
    }
    return { ok: false, status: response.status, message: data.error || null }
  } catch {
    return { ok: false, status: 0, message: null }
  }
}

// settingsLotteryGet — конфиг лотереи + read-инфо (пул билетов / последний
// розыгрыш / история победителей). Этап 3, Настройки → Лотерея. Полный гард →
// withToken. Пустое тело. 200 → {ok:true, enabled, prize, min_amount, prize_min,
// prize_max, min_min, min_max, pool_tickets, last_draw, history}. ⚠️ Действия
// «провести розыгрыш» НЕТ — оно остаётся в боте.
export async function settingsLotteryGet() {
  try {
    const response = await post('/settings/lottery/get', {}, true)
    if (response.status === 200) {
      return await readJson(response)
    }
    return { error: true, status: response.status }
  } catch {
    return { error: true, status: 0 }
  }
}

// settingsLotterySet — сохранить конфиг лотереи {enabled, prize, min_amount}.
// Бэк — авторитет валидации (prize 1..100000, min 0..100000). Подтверждение/
// красная — ТОЛЬКО при выключении toggle (off), на UI. 200 → {ok:true, ...payload
// + read-инфо}. 400 → {ok:false, status:400, message}. 401 → протух (→ PIN).
export async function settingsLotterySet(payload) {
  try {
    const response = await post('/settings/lottery/set', payload, true)
    const data = await readJson(response)
    if (response.status === 200 && data.ok) {
      return data
    }
    return { ok: false, status: response.status, message: data.error || null }
  } catch {
    return { ok: false, status: 0, message: null }
  }
}

// ──────────────── Бонус к пополнению (deposit bonus, тиры) ────────────────
// Этап 3, Настройки → Бонус деп. Флаг deposit_bonus_enabled + таблица тиров
// (id/min_amount/percent/is_enabled). Гранулярный CRUD: get + toggle + rule/add
// + rule/delete (EDIT нет — правка = удалить+добавить). Полный гард → withToken.
// Бэк — авторитет валидации (min>0 целое, percent 0..100, порог уникален).

// settingsDepositBonusGet — флаг + список тиров (вкл выключенные). 200 →
// {ok:true, enabled, rules:[{id,min_amount,percent,is_enabled}], percent_min,
// percent_max, min_amount_max}.
export async function settingsDepositBonusGet() {
  try {
    const response = await post('/settings/deposit-bonus/get', {}, true)
    if (response.status === 200) {
      return await readJson(response)
    }
    return { error: true, status: response.status }
  } catch {
    return { error: true, status: 0 }
  }
}

// settingsDepositBonusToggle — вкл/выкл акции {enabled}. Подтв+красная при
// выключении — на UI. 200 → {ok:true, ...payload}. 401 → протух.
export async function settingsDepositBonusToggle(enabled) {
  try {
    const response = await post('/settings/deposit-bonus/toggle', { enabled }, true)
    const data = await readJson(response)
    if (response.status === 200 && data.ok) {
      return data
    }
    return { ok: false, status: response.status, message: data.error || null }
  } catch {
    return { ok: false, status: 0, message: null }
  }
}

// settingsDepositBonusRuleAdd — новый тир {min_amount, percent}. Бэк-валидация
// (min>0 целое, percent 0..100, уникальный порог). 200 → {ok:true, ...payload}.
export async function settingsDepositBonusRuleAdd(payload) {
  try {
    const response = await post('/settings/deposit-bonus/rule/add', payload, true)
    const data = await readJson(response)
    if (response.status === 200 && data.ok) {
      return data
    }
    return { ok: false, status: response.status, message: data.error || null }
  } catch {
    return { ok: false, status: 0, message: null }
  }
}

// settingsDepositBonusRuleDelete — удалить тир {id} (НЕОБРАТИМО → подтв+красная
// на UI). 200 → {ok:true, ...payload}. 404 → правило не найдено. 401 → протух.
export async function settingsDepositBonusRuleDelete(id) {
  try {
    const response = await post('/settings/deposit-bonus/rule/delete', { id }, true)
    const data = await readJson(response)
    if (response.status === 200 && data.ok) {
      return data
    }
    return { ok: false, status: response.status, message: data.error || null }
  } catch {
    return { ok: false, status: 0, message: null }
  }
}

// settingsDepositBonusRuleEdit — правка тира {id, min_amount, percent}. БЕЗ
// подтверждения (правка значений). Бэк-валидация (та же, что add: min>0 целое,
// percent 0..100, +уникальность порога ИСКЛЮЧАЯ свой id). 200 → {ok:true,
// ...payload}. 404 → правило не найдено. 401 → протух.
export async function settingsDepositBonusRuleEdit(id, min_amount, percent) {
  try {
    const response = await post('/settings/deposit-bonus/rule/edit', { id, min_amount, percent }, true)
    const data = await readJson(response)
    if (response.status === 200 && data.ok) {
      return data
    }
    return { ok: false, status: response.status, message: data.error || null }
  } catch {
    return { ok: false, status: 0, message: null }
  }
}

// ─────────────────────────── Скидки на товары ───────────────────────────────
// ⚠️ ЦЕНОВАЯ ЗОНА: set/remove двигают реальную цену в боте И витрине → на UI
// подтв+красная. show-toggle — только бейдж, без подтв (но audit на бэке).

// settingsDiscountsGet — все товары со статусом. 200 → {ok:true,
// items:[{category,product_id,name,label,percent,active,show}], percent_min,
// percent_max}. 401 → протух.
export async function settingsDiscountsGet() {
  try {
    const response = await post('/settings/discounts/get', {}, true)
    if (response.status === 200) {
      return await readJson(response)
    }
    return { error: true, status: response.status }
  } catch {
    return { error: true, status: 0 }
  }
}

// settingsDiscountSet — задать/изменить скидку {category, product_id, percent}.
// ⚠️ ЦЕНОВАЯ ЗОНА (подтв+красная на UI). Бэк-валидация 0..99. 200 →
// {ok:true, ...payload}. 401 → протух.
export async function settingsDiscountSet({ category, product_id, percent }) {
  try {
    const response = await post('/settings/discounts/set', { category, product_id, percent }, true)
    const data = await readJson(response)
    if (response.status === 200 && data.ok) {
      return data
    }
    return { ok: false, status: response.status, message: data.error || null }
  } catch {
    return { ok: false, status: 0, message: null }
  }
}

// settingsDiscountRemove — убрать скидку {category, product_id} (DELETE строки).
// ⚠️ ЦЕНОВАЯ ЗОНА (подтв+красная на UI). 200 → {ok:true, ...payload}. 401 → протух.
export async function settingsDiscountRemove({ category, product_id }) {
  try {
    const response = await post('/settings/discounts/remove', { category, product_id }, true)
    const data = await readJson(response)
    if (response.status === 200 && data.ok) {
      return data
    }
    return { ok: false, status: response.status, message: data.error || null }
  } catch {
    return { ok: false, status: 0, message: null }
  }
}

// settingsDiscountShowToggle — тумблер бейджа -X% {category, product_id, show}.
// Цену НЕ двигает (без подтв). 200 → {ok:true, ...payload}. 401 → протух.
export async function settingsDiscountShowToggle({ category, product_id, show }) {
  try {
    const response = await post('/settings/discounts/show-toggle', { category, product_id, show }, true)
    const data = await readJson(response)
    if (response.status === 200 && data.ok) {
      return data
    }
    return { ok: false, status: response.status, message: data.error || null }
  } catch {
    return { ok: false, status: 0, message: null }
  }
}

// === Этап 3, Настройки → Шлюзы ===
// ⚠️ СЕКРЕТЫ: бэк НИКОГДА не отдаёт config (ключи API). Эти функции работают
// только с не-секретными полями (toggle/комиссии/витринные).

// settingsGatewaysGet — все шлюзы (только не-секретное). 200 → {ok:true,
// items:[{gateway_id,name,is_enabled,fee_percent,webapp_name,webapp_fee,
// fee_type,webapp_sort_order,webapp_featured,min_amount}], fee_min, fee_max,
// min_amount_min, min_amount_max, sort_min, sort_max, fee_types}. 401 → протух.
export async function settingsGatewaysGet() {
  try {
    const response = await post('/settings/gateways/get', {}, true)
    if (response.status === 200) {
      return await readJson(response)
    }
    return { error: true, status: response.status }
  } catch {
    return { error: true, status: 0 }
  }
}

// settingsGatewayToggle — вкл/выкл шлюз {gateway_id, enabled}. Guard: нельзя
// выключить последний активный → 409. Подтв+красная на UI. 200 → {ok:true, ...payload}.
export async function settingsGatewayToggle({ gateway_id, enabled }) {
  try {
    const response = await post('/settings/gateways/toggle', { gateway_id, enabled }, true)
    const data = await readJson(response)
    if (response.status === 200 && data.ok) {
      return data
    }
    return { ok: false, status: response.status, message: data.error || null }
  } catch {
    return { ok: false, status: 0, message: null }
  }
}

// settingsGatewayPricing — комиссии {gateway_id, field, value}. field ∈
// {fee_percent, webapp_fee, fee_type}. ⚠️ ЦЕНОВАЯ ЗОНА (подтв+красная на UI).
// Бэк-валидация (fee 0..100, тип ∈ additive/informational). 200 → {ok:true, ...payload}.
export async function settingsGatewayPricing({ gateway_id, field, value }) {
  try {
    const response = await post('/settings/gateways/pricing', { gateway_id, field, value }, true)
    const data = await readJson(response)
    if (response.status === 200 && data.ok) {
      return data
    }
    return { ok: false, status: response.status, message: data.error || null }
  } catch {
    return { ok: false, status: 0, message: null }
  }
}

// settingsGatewayDisplay — витринные поля {gateway_id, field, value}. field ∈
// {webapp_name, min_amount, webapp_sort_order}. UX (без подтв). Цену не двигает.
// 200 → {ok:true, ...payload}.
export async function settingsGatewayDisplay({ gateway_id, field, value }) {
  try {
    const response = await post('/settings/gateways/display', { gateway_id, field, value }, true)
    const data = await readJson(response)
    if (response.status === 200 && data.ok) {
      return data
    }
    return { ok: false, status: response.status, message: data.error || null }
  } catch {
    return { ok: false, status: 0, message: null }
  }
}

// settingsGatewayFeatured — отметить шлюз «рекомендуемым» {gateway_id}.
// Эксклюзивный флаг (сбрасывает у остальных). UX (без подтв). 200 → {ok:true, ...payload}.
export async function settingsGatewayFeatured({ gateway_id }) {
  try {
    const response = await post('/settings/gateways/featured', { gateway_id }, true)
    const data = await readJson(response)
    if (response.status === 200 && data.ok) {
      return data
    }
    return { ok: false, status: response.status, message: data.error || null }
  } catch {
    return { ok: false, status: 0, message: null }
  }
}

// === Этап 3, Настройки → Наценки (8-й подраздел) ===
// ⚠️⚠️ ЦЕНОВАЯ ЗОНА + запись в исходник settings.py на бэке. Наценки двигают
// РЕАЛЬНУЮ цену всех товаров категории в боте И витрине; referral — % реф-выплат.

// settingsCommissionsGet — 7 наценок + referral. 200 → {ok:true,
// items:[{key,label,percent,is_price}], percent_min, percent_max}. 401 → протух.
export async function settingsCommissionsGet() {
  try {
    const response = await post('/settings/commissions/get', {}, true)
    if (response.status === 200) {
      return await readJson(response)
    }
    return { error: true, status: response.status }
  } catch {
    return { error: true, status: 0 }
  }
}

// settingsCommissionSet — задать наценку/referral {key, percent}. ⚠️ ЦЕНОВАЯ
// ЗОНА (подтв+красная на UI). Бэк-валидация int 0..100. 200 → {ok:true, ...payload}.
export async function settingsCommissionSet({ key, percent }) {
  try {
    const response = await post('/settings/commissions/set', { key, percent }, true)
    const data = await readJson(response)
    if (response.status === 200 && data.ok) {
      return data
    }
    return { ok: false, status: response.status, message: data.error || null }
  } catch {
    return { ok: false, status: 0, message: null }
  }
}

// Хелперы для UI. Значение токена наружу НЕ отдаём — только факт наличия.
export function hasToken() {
  return _token !== null
}

export function clearToken() {
  _token = null
}
